import React, { useEffect } from 'react';
import { App as AntApp } from 'antd';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import { ToastHost } from '@spark';

import ReloadWhenStale from '@/app/ReloadWhenStale';
import SideBar from '@/app/layout/SideBar';
import { GlobalProvider } from '@/app/providers/globalState';
import { AuthProvider, RequireSaas, NO_ACCESS_PATH } from '@/features/auth/AuthContext';
import AuthBoundary from '@/features/auth/components/AuthBoundary';
import NoAccessPage from '@/features/auth/components/NoAccessPage';
import PendingApproval from '@/features/auth/components/PendingApproval';
import { CookieConsentBanner } from '@/features/cookie-consent';
import { WalkthroughProvider } from '@/features/walkthrough';
import {
    PRIVACY_POLICY_PATH,
    TERMS_OF_USE_PATH,
    PrivacyPolicyPage,
    TermsOfUsePage,
} from '@/features/legal';
import { WaitlistLanding, WaitlistApplyPage } from '@/features/waiting-list';
import CreateWorkspacePage from '@/features/workspaces/components/CreateWorkspacePage';
import JoinWorkspacePage from '@/features/workspaces/components/JoinWorkspacePage';
import Login from '@/pages/Login.jsx';

/** Browser tab title: the first path segment title-cased, or the task name on `/task/...`. */
export function pageTitle(pathname) {
    const segments = pathname.split('/').filter(Boolean);
    const name = segments[0] === 'task'
        ? decodeURIComponent(segments.at(-1))
        : (segments[0] || 'projects').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `${name} - Specific AI`;
}

/**
 * The one route table shared by both browser entries (`src/app/main.jsx`
 * for `index.html`, `src/index.jsx` for `public/index-fullstory.html`).
 * Production serves the FullStory entry, so a route that only existed in
 * one table used to work in development and 404 (or spin forever) live.
 *
 * Everything sits behind `AuthBoundary`: no page mounts until the session
 * bootstrap has settled and any bootstrap-driven redirect has been applied.
 */
export function AppRoutes() {
    const { pathname } = useLocation();
    useEffect(() => {
        document.title = pageTitle(pathname);
    }, [pathname]);

    return (
        <AuthBoundary>
            <Routes>
                {/* Enterprise SSO only: the self-service signup/verify/forgot flows were removed. */}
                <Route path="/login" element={<Login />} />
                {/* Waiting list (SaaS only): public landing + the authenticated questionnaire, both outside the SideBar shell. */}
                <Route
                    path="/waitlist"
                    element={(
                        <RequireSaas>
                            <WaitlistLanding />
                        </RequireSaas>
                    )}
                />
                <Route
                    path="/waitlist/apply"
                    element={(
                        <RequireSaas>
                            <WaitlistApplyPage />
                        </RequireSaas>
                    )}
                />
                {/* Self-hosted counterpart: strangers have no waiting list and can only be invited by a workspace admin. */}
                <Route path={NO_ACCESS_PATH} element={<NoAccessPage />} />
                <Route path="/pending-approval" element={<PendingApproval />} />
                {/* Onboarding wizard for an approved Creator without a workspace; same full-page shell as the waiting list. */}
                <Route path="/create-workspace" element={<CreateWorkspacePage />} />
                <Route path="/join-workspace" element={<JoinWorkspacePage />} />
                <Route
                    path="/*"
                    element={(
                        <GlobalProvider>
                            <SideBar />
                        </GlobalProvider>
                    )}
                />
            </Routes>
        </AuthBoundary>
    );
}

/**
 * Top-level route table. The legal documents sit *outside* `AuthProvider`
 * on purpose: the provider redirects any signed-in person without a
 * workspace (to the wizard, the waiting list or `/admin`) regardless of the
 * URL, and the cookie banner links to the Privacy Policy before anyone has
 * signed in at all. Everything else keeps going through the session
 * bootstrap in `AppRoutes`.
 */
export function RootRoutes() {
    return (
        <Routes>
            <Route path={PRIVACY_POLICY_PATH} element={<PrivacyPolicyPage />} />
            <Route path={TERMS_OF_USE_PATH} element={<TermsOfUsePage />} />
            <Route
                path="/*"
                element={(
                    <AuthProvider>
                        <WalkthroughProvider>
                            <AppRoutes />
                        </WalkthroughProvider>
                    </AuthProvider>
                )}
            />
        </Routes>
    );
}

export default function App() {
    return (
        <div className="app-wrapper" style={{ height: '100%', overflow: 'hidden' }}>
            <BrowserRouter>
                <ReloadWhenStale />
                <AntApp>
                    <RootRoutes />
                </AntApp>
            </BrowserRouter>
            <ToastHost />
            <CookieConsentBanner />
        </div>
    );
}
